/**
 * Nested-scroll guard for content portaled OUT of a modal Radix Dialog.
 *
 * The problem
 * -----------
 * A modal Radix Dialog (our `Dialog`, `Sheet`, and therefore `FloatingDrawer`)
 * wraps its content in `react-remove-scroll`, which registers `wheel` and
 * `touchmove` listeners on `document` and `preventDefault()`s any event whose
 * target is not inside the dialog's own node. Radix `PopoverContent` is
 * portaled to `document.body`, so it sits OUTSIDE that node — every wheel /
 * two-finger-trackpad gesture over a popover opened from inside a drawer is
 * cancelled, and its content cannot be scrolled at all. Symptom: the element is
 * genuinely scrollable (`scrollHeight > clientHeight`, scrollbar drag and
 * keyboard both work) but wheel and trackpad do nothing.
 *
 * Radix `Select` / `DropdownMenu` are immune because each mounts its OWN
 * `RemoveScroll` whose allowlist is its own content. A `modal` Popover does the
 * same — but `modal` cannot be used inside `FloatingDrawer`, because two
 * competing scroll locks / dismissable layers fight over outside-click, which
 * is why `SearchableSelect` is mounted with `modal={false}` there.
 *
 * The fix
 * -------
 * `react-remove-scroll` listens in the BUBBLE phase on `document`, so stopping
 * propagation before the event gets there leaves the browser free to perform
 * its normal default scroll — no manual scrollTop maths, no synthetic events.
 *
 * Crucially this is NOT a blanket `stopPropagation`. We only stop when some
 * element between the event target and the popover root can actually scroll in
 * the gesture's direction. If nothing can, the event is left alone and
 * `react-remove-scroll` still blocks it — otherwise wheeling over a
 * non-scrollable popover would chain up to the document and scroll the page
 * behind the modal, which is the very thing the scroll lock exists to prevent.
 */

const SCROLLABLE_OVERFLOW = /^(auto|scroll|overlay)$/;

/** Treat sub-pixel layout rounding as "no overflow". */
const OVERFLOW_EPSILON = 1;

function isScrollableIn(
  el: Element,
  axis: 'y' | 'x',
  style: CSSStyleDeclaration,
): boolean {
  const overflow = axis === 'y' ? style.overflowY : style.overflowX;
  if (!SCROLLABLE_OVERFLOW.test(overflow)) return false;
  const scrollSize = axis === 'y' ? el.scrollHeight : el.scrollWidth;
  const clientSize = axis === 'y' ? el.clientHeight : el.clientWidth;
  return scrollSize - clientSize > OVERFLOW_EPSILON;
}

function canMove(el: Element, axis: 'y' | 'x', delta: number): boolean {
  const pos = axis === 'y' ? el.scrollTop : el.scrollLeft;
  const scrollSize = axis === 'y' ? el.scrollHeight : el.scrollWidth;
  const clientSize = axis === 'y' ? el.clientHeight : el.clientWidth;
  if (delta > 0) return pos + clientSize < scrollSize - OVERFLOW_EPSILON;
  if (delta < 0) return pos > OVERFLOW_EPSILON;
  // Direction unknown (touchmove) — any remaining scroll range counts.
  return true;
}

function containsOverscroll(style: CSSStyleDeclaration, axis: 'y' | 'x'): boolean {
  const value =
    (axis === 'y' ? style.overscrollBehaviorY : style.overscrollBehaviorX) ||
    style.overscrollBehavior;
  return value === 'contain' || value === 'none';
}

/**
 * Walks from `target` up to (and including) `boundary`, looking for a scroll
 * container that can still move in the gesture's direction.
 *
 * @param deltaY/deltaX pass 0 when the direction is unknown (touchmove).
 */
export function hasScrollableAncestorWithin(
  target: EventTarget | null,
  boundary: Element | null,
  deltaY: number,
  deltaX = 0,
): boolean {
  if (!boundary || !(target instanceof Element)) return false;
  if (!boundary.contains(target)) return false;

  let node: Element | null = target;
  while (node) {
    // getComputedStyle throws on detached/odd nodes in some engines.
    let style: CSSStyleDeclaration | null = null;
    try {
      style = window.getComputedStyle(node);
    } catch {
      style = null;
    }

    if (style) {
      const axes: Array<['y' | 'x', number]> = [
        ['y', deltaY],
        ['x', deltaX],
      ];
      for (const [axis, delta] of axes) {
        // A zero delta on BOTH axes means "unknown direction" — probe the
        // primary (vertical) axis. A zero delta on only one axis means the
        // gesture simply isn't on that axis, so skip it.
        if (delta === 0 && !(axis === 'y' && deltaX === 0)) continue;
        if (!isScrollableIn(node, axis, style)) continue;
        if (canMove(node, axis, delta)) return true;
        // This container is at its end. `overscroll-behavior: contain` means
        // it deliberately refuses to hand the gesture to an ancestor, so stop
        // looking — the correct outcome is "nothing scrolls".
        if (containsOverscroll(style, axis)) return false;
      }
    }

    if (node === boundary) break;
    node = node.parentElement;
  }

  return false;
}

export interface NestedScrollGuardHandlers {
  onWheelCapture: (event: React.WheelEvent) => void;
  onTouchMoveCapture: (event: React.TouchEvent) => void;
}

/**
 * Handlers to spread onto a portaled container (e.g. Radix `PopoverContent`)
 * so wheel / trackpad / touch scrolling works for scrollable content inside it
 * even when an ancestor modal has installed `react-remove-scroll`.
 *
 * Attach to wheel/touchmove ONLY. Pointer events must keep bubbling —
 * `FloatingDrawer` relies on `pointerdown` reaching its capture-phase document
 * listener to decide whether an outside click dismisses the nested layer or
 * the drawer itself.
 */
export function createNestedScrollGuard(): NestedScrollGuardHandlers {
  return {
    onWheelCapture: (event) => {
      if (
        hasScrollableAncestorWithin(
          event.target,
          event.currentTarget as Element,
          event.deltaY,
          event.deltaX,
        )
      ) {
        event.stopPropagation();
      }
    },
    onTouchMoveCapture: (event) => {
      // Touch direction is not available without tracking touchstart; allow the
      // gesture whenever the subtree has any usable scroll range.
      if (hasScrollableAncestorWithin(event.target, event.currentTarget as Element, 0, 0)) {
        event.stopPropagation();
      }
    },
  };
}
